<script lang="ts">
  import { untrack } from 'svelte';
  import Dialog from './Dialog.svelte';
  import EditableTitle from './EditableTitle.svelte';
  import CityPicker from './CityPicker.svelte';
  import Icon from './Icon.svelte';
  import { photoUrl, updatePoi, setPoiGroupMembership, refreshPoiDetails } from './api';
  import { findTodayHoursLine, isOpenNow } from './opening-hours';
  import OpenNowBadge from './OpenNowBadge.svelte';
  import { formatDistanceFrom, haversineDistanceM } from '~/shared/group-actions.js';
  import { formatTypeLabel } from '~/shared/place-type.js';
  import { appleMapsLocateUrl, googleMapsPlaceUrl } from '~/shared/routing.js';
  import { routeActionLabel } from '~/shared/route-handoff.js';
  import type { PoiView, GroupView, TripCityView } from '~/shared/api.js';

  let {
    poi,
    tripToken,
    editable,
    groups,
    cities = [],
    activeCity = null,
    routeEmpty = true,
    inRoute = false,
    isCover = false,
    hoursLocale = null,
    onClose,
    onUpdated,
    onRemove,
    onNearby,
    onAddToRoute,
    onToggleCover,
  }: {
    poi: PoiView;
    tripToken: string;
    editable: boolean;
    groups: GroupView[];
    cities?: TripCityView[];
    activeCity?: TripCityView | null;
    /** The language the hours were fetched in — passed to the
     *  today-matcher so it formats the comparison weekday in the
     *  same locale (e.g., 月曜日 vs Monday). */
    hoursLocale?: string | null;
    /** True when the trip's route has zero stops — labels the primary
     *  body button "Route" instead of "Add to route". */
    routeEmpty?: boolean;
    /** True when this POI is already in the route — disables the
     *  primary body button. */
    inRoute?: boolean;
    /** True when this POI is the trip's current cover. The bottom-right
     *  photo button switches from "Set as cover" to "Unset" copy + a
     *  filled star. */
    isCover?: boolean;
    onClose: () => void;
    onUpdated: (poi: PoiView) => void;
    onRemove: (poi: PoiView) => void | Promise<void>;
    onNearby: (poi: PoiView) => void;
    /** Append this POI to the trip's route. Surfaces the primary
     *  "Route" / "Add to route" body button. */
    onAddToRoute?: (poi: PoiView) => void;
    /** Toggle this POI as the trip's dashboard cover. When set, parent
     *  PATCHes coverPoiId on the trip. */
    onToggleCover?: () => void | Promise<void>;
  } = $props();

  const displayName = $derived(poi.customName ?? poi.name);
  const src = $derived(photoUrl(poi.photoReference, 1080));
  const appleLocateUrl = $derived(appleMapsLocateUrl(poi));
  const googlePlaceUrl = $derived(googleMapsPlaceUrl(poi));

  // Lazy refresh of Places Details when the sheet opens a stale POI.
  // "Stale" = never refreshed since migration 012 landed (NULL), or the
  // last fetch was more than 30 days ago. Pro-tier Details call;
  // rate-limited server-side. View-only viewers skip — the endpoint
  // requires editable.
  const STALE_MS = 30 * 24 * 60 * 60_000;
  $effect(() => {
    if (!editable) return;
    const fetchedAt = poi.detailsFetchedAt;
    if (fetchedAt !== null && Date.now() - fetchedAt < STALE_MS) return;

    const targetId = poi.id;
    const controller = new AbortController();
    (async () => {
      try {
        const updated = await refreshPoiDetails(tripToken, targetId, controller.signal);
        onUpdated(updated);
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        // Best-effort — silent on failure. The user keeps the cached
        // view; a future open will retry.
      }
    })();
    return () => controller.abort();
  });

  const distanceLabel = $derived(
    activeCity ? formatDistanceFrom(haversineDistanceM(activeCity, poi), activeCity.name) : null,
  );
  const typeLabel = $derived(formatTypeLabel(poi.category));

  // Hours UI: today-by-default, expand for the full week. When we
  // can't match today (locale mismatch, unusual day names), fall back
  // to the full schedule with no collapse — keeps the user informed
  // rather than hiding everything.
  const todayHoursLine = $derived(
    poi.openingHours ? findTodayHoursLine(poi.openingHours, hoursLocale) : null,
  );
  // Open-now status. Null = we don't have enough data (legacy row or
  // no published periods) — badge hides. Re-derives when `poi` changes
  // so a refresh-details that backfills periods flips the badge live.
  const openStatus = $derived(isOpenNow(poi.openingPeriods, poi.utcOffsetMinutes));
  let hoursExpanded: boolean = $state(false);
  $effect(() => {
    // Reset expansion when the sheet switches to a different POI.
    void poi.id;
    hoursExpanded = false;
  });

  async function onCityPick(nextCityId: number | null) {
    const updated = await updatePoi(tripToken, poi.id, { cityId: nextCityId });
    onUpdated(updated);
  }

  const routeButtonLabel = $derived(routeActionLabel({ inRoute, routeEmpty }));

  async function handleRemove() {
    await onRemove(poi);
    onClose();
  }

  let groupsSaving: boolean = $state(false);
  async function toggleGroupMembership(groupId: number) {
    if (groupsSaving) return;
    const next = poi.groupIds.includes(groupId)
      ? poi.groupIds.filter((id) => id !== groupId)
      : [...poi.groupIds, groupId];
    groupsSaving = true;
    try {
      const updated = await setPoiGroupMembership(tripToken, poi.id, next);
      onUpdated(updated);
    } finally {
      groupsSaving = false;
    }
  }

  let resettingName = $state(false);

  async function onNameCommit(next: string) {
    // Typing exactly the original Google name → clear the override.
    const customName = next === poi.name ? null : next;
    const updated = await updatePoi(tripToken, poi.id, { customName });
    onUpdated(updated);
  }

  async function resetName() {
    if (resettingName || poi.customName === null) return;
    resettingName = true;
    try {
      const updated = await updatePoi(tripToken, poi.id, { customName: null });
      onUpdated(updated);
    } catch (e) {
      console.error('Failed to reset name:', e);
    } finally {
      resettingName = false;
    }
  }

  // Notes auto-save ───────────────────────────────────────────────────────
  let notesValue: string = $state(untrack(() => poi.notes ?? ''));
  let notesLastSaved: string = $state(untrack(() => poi.notes ?? ''));
  let notesSaving = $state(false);
  let savedFlash = $state(false);
  let savedFlashTimer: ReturnType<typeof setTimeout> | null = null;

  $effect(() => {
    const remote = poi.notes ?? '';
    untrack(() => {
      if (remote !== notesLastSaved) {
        notesValue = remote;
        notesLastSaved = remote;
      }
    });
  });

  $effect(() => {
    if (!editable) return;
    const trimmed = notesValue.trim();
    if (trimmed === notesLastSaved.trim()) return;
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      notesSaving = true;
      try {
        const next = trimmed === '' ? null : trimmed;
        const updated = await updatePoi(tripToken, poi.id, { notes: next }, controller.signal);
        notesLastSaved = next ?? '';
        onUpdated(updated);
        flashSaved();
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        console.error('Failed to save notes:', e);
      } finally {
        notesSaving = false;
      }
    }, 750);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  });

  $effect(() => () => {
    if (savedFlashTimer) clearTimeout(savedFlashTimer);
  });

  function flashSaved() {
    savedFlash = true;
    if (savedFlashTimer) clearTimeout(savedFlashTimer);
    savedFlashTimer = setTimeout(() => (savedFlash = false), 1600);
  }

  // Swipe-to-dismiss on the drag handle (mobile bottom sheet) ─────────────
  let dragOffset = $state(0);
  let dragging = $state(false);
  let dragStartY = 0;
  let dragPointerId: number | null = null;
  const DISMISS_THRESHOLD_PX = 100;

  function onHandlePointerDown(e: PointerEvent) {
    dragging = true;
    dragStartY = e.clientY;
    dragPointerId = e.pointerId;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onHandlePointerMove(e: PointerEvent) {
    if (!dragging || e.pointerId !== dragPointerId) return;
    const delta = e.clientY - dragStartY;
    dragOffset = delta > 0 ? delta : 0;
  }
  function onHandlePointerUp(e: PointerEvent) {
    if (!dragging || e.pointerId !== dragPointerId) return;
    const released = dragOffset;
    dragging = false;
    dragPointerId = null;
    dragOffset = 0;
    // Only dismiss on a clean release past threshold; cancel events
    // (which also route here) always reset to 0.
    if (e.type === 'pointerup' && released > DISMISS_THRESHOLD_PX) {
      onClose();
    }
  }
</script>

<Dialog {onClose} variant="sheet">
  <div
    class="detail"
    class:dragging
    role="dialog"
    aria-modal="true"
    aria-label={displayName}
    style="transform: translateY({dragOffset}px);"
  >
    <button
      type="button"
      class="drag-handle"
      tabindex="-1"
      aria-label="Swipe down or tap to close"
      onpointerdown={onHandlePointerDown}
      onpointermove={onHandlePointerMove}
      onpointerup={onHandlePointerUp}
      onpointercancel={onHandlePointerUp}
      onclick={onClose}
    >
      <span class="grabber"></span>
    </button>
    <div class="photo-wrap">
      {#if src}
        <img src={src} alt="" />
      {:else}
        <div class="no-photo"></div>
      {/if}
      {#if poi.rating !== null}
        <span class="photo-badge rating-badge">
          <Icon name="star" size={11} />
          <span class="tabular-nums">{poi.rating.toFixed(1)}</span>
        </span>
      {/if}
      {#if editable && src && onToggleCover}
        <button
          type="button"
          class="cover-badge"
          class:cover-badge--active={isCover}
          aria-label={isCover ? 'Remove as trip cover' : 'Set as trip cover'}
          title={isCover ? 'Remove as trip cover' : 'Set as trip cover'}
          aria-pressed={isCover}
          onclick={() => onToggleCover?.()}
        >
          <Icon name="star" size={14} strokeWidth={2.25} />
          <span>{isCover ? 'Cover' : 'Set as cover'}</span>
        </button>
      {/if}
      {#if openStatus !== null}
        <OpenNowBadge status={openStatus} />
      {/if}
    </div>
    <div class="photo-overlay">
      <button class="close-btn" type="button" aria-label="Close" onclick={onClose}>
        <Icon name="x" size={16} />
      </button>
    </div>

    <div class="body">
      <EditableTitle
        as="h2"
        headingClass="title"
        value={displayName}
        editable={editable}
        ariaLabel="Place name"
        onCommit={onNameCommit}
      />
      {#if poi.customName !== null && poi.customName !== poi.name}
        <p class="orig-name">
          <span>orig. {poi.name}</span>
          {#if editable}
            <button type="button" class="reset-link" onclick={resetName}>Reset</button>
          {/if}
        </p>
      {/if}

      <div class="meta">
        {#if typeLabel}
          <span>{typeLabel}</span>
        {/if}
        {#if distanceLabel}
          {#if typeLabel}<span aria-hidden="true">·</span>{/if}
          <span>{distanceLabel}</span>
        {/if}
      </div>

      {#if poi.formattedAddress}
        <p class="address">{poi.formattedAddress}</p>
      {/if}

      {#if poi.openingHours && poi.openingHours.length > 0}
        <section class="hours" aria-label="Opening hours">
          <h3 class="hours-title">Hours</h3>
          {#if todayHoursLine && !hoursExpanded}
            <div class="hours-summary">
              <span class="hours-today">{todayHoursLine}</span>
              {#if poi.openingHours.length > 1}
                <button
                  type="button"
                  class="hours-toggle"
                  aria-expanded="false"
                  onclick={() => (hoursExpanded = true)}
                >
                  <span>See full schedule</span>
                  <Icon name="chevron-down" size={14} strokeWidth={2.25} />
                </button>
              {/if}
            </div>
          {:else}
            <ul class="hours-list">
              {#each poi.openingHours as line (line)}
                <li class:hours-row--today={line === todayHoursLine}>{line}</li>
              {/each}
            </ul>
            {#if todayHoursLine}
              <button
                type="button"
                class="hours-toggle hours-toggle--hide"
                aria-expanded="true"
                onclick={() => (hoursExpanded = false)}
              >
                <span>Hide schedule</span>
                <Icon name="chevron-up" size={14} strokeWidth={2.25} />
              </button>
            {/if}
          {/if}
        </section>
      {/if}

      <hr class="section-divider" />

      {#if cities.length > 0 || poi.cityId !== null}
        <CityPicker
          selectedCityId={poi.cityId}
          cities={cities}
          editable={editable}
          onPick={onCityPick}
        />
      {/if}

      {#if groups.length > 0}
        <div class="groups">
          {#each groups as g (g.id)}
            {@const isMember = poi.groupIds.includes(g.id)}
            {#if editable}
              <button
                type="button"
                class="group-chip"
                class:active={isMember}
                aria-pressed={isMember}
                disabled={groupsSaving}
                onclick={() => toggleGroupMembership(g.id)}
              >
                <span class="dot dot--{g.tint}"></span>
                <span>{g.name}</span>
              </button>
            {:else if isMember}
              <span class="group-chip active">
                <span class="dot dot--{g.tint}"></span>
                <span>{g.name}</span>
              </span>
            {/if}
          {/each}
        </div>
      {/if}

      <section class="notes-section">
        <header class="notes-head">
          <h3>Notes</h3>
          {#if savedFlash}
            <span class="photo-badge notes-saved">Saved</span>
          {:else if notesSaving}
            <span class="photo-badge notes-saved">Saving…</span>
          {/if}
        </header>
        <textarea
          class="notes"
          bind:value={notesValue}
          placeholder={editable ? 'Must-order dishes, hours, what to do here…' : ''}
          readonly={!editable}
          rows="4"
          maxlength="2000"
        ></textarea>
      </section>

    </div>

    <footer class="detail-footer">
      <!-- Quick-action bar: secondary actions as icon+label tiles, the
           usual sheet-footer pattern. Equal-width grid so 3 (view-only)
           or 4 (editable) actions both look intentional. -->
      <div class="quick-actions" role="group" aria-label="More actions">
        <a class="quick-action" href={googlePlaceUrl} target="_blank" rel="noopener noreferrer">
          <Icon name="info" size={18} />
          <span>Details</span>
        </a>
        <a class="quick-action" href={appleLocateUrl} target="_blank" rel="noopener noreferrer">
          <Icon name="map-pin" size={18} />
          <span>Apple Maps</span>
        </a>
        <button type="button" class="quick-action" onclick={() => onNearby(poi)}>
          <Icon name="sparkles" size={18} />
          <span>Nearby</span>
        </button>
        {#if editable}
          <button
            type="button"
            class="quick-action quick-action--destructive"
            onclick={handleRemove}
          >
            <Icon name="trash" size={18} />
            <span>Remove</span>
          </button>
        {/if}
      </div>
      {#if onAddToRoute}
        <button
          class="btn btn-primary nav-btn"
          type="button"
          disabled={inRoute}
          onclick={() => {
            onAddToRoute(poi);
            onClose();
          }}
        >
          <span>{routeButtonLabel}</span>
        </button>
      {/if}
    </footer>
  </div>
</Dialog>

<style>
  .detail {
    position: relative;
    background: var(--color-canvas);
    width: 100%;
    height: calc(100dvh - 56px);
    max-height: calc(100dvh - 56px);
    border-radius: var(--radius-md) var(--radius-md) 0 0;
    box-shadow: rgba(15, 15, 15, 0.2) 0 -8px 32px -4px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    touch-action: pan-y;
    transition: transform 220ms cubic-bezier(0.32, 0.72, 0, 1);
  }
  .detail.dragging {
    transition: none;
  }
  @media (min-width: 768px) {
    .detail {
      height: auto;
      max-height: 85vh;
      max-width: 720px;
      border-radius: var(--radius-md);
      box-shadow:
        rgba(0, 0, 0, 0.02) 0 0 0 1px,
        rgba(0, 0, 0, 0.04) 0 2px 6px 0,
        rgba(0, 0, 0, 0.10) 0 4px 8px 0;
    }
  }

  .drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    flex: none;
    background: var(--color-canvas);
    cursor: grab;
    touch-action: none;
    border: 0;
  }
  .drag-handle:active { cursor: grabbing; }
  .grabber {
    width: 36px;
    height: 4px;
    background: var(--color-border-strong);
    border-radius: 9999px;
    pointer-events: none;
  }
  @media (min-width: 768px) {
    /* Drag-to-dismiss is a mobile-sheet pattern; on desktop the modal is
       centered so the handle reads weirdly. Hide it. */
    .drag-handle { display: none; }
  }

  .photo-wrap {
    position: relative;
    aspect-ratio: 16 / 10;
    background: var(--color-surface-strong);
    overflow: hidden;
    flex: none;
  }
  /* On desktop the modal caps at 85vh; a 16:10 hero at 720px wide eats
     ~450px and leaves no room for notes without scrolling. Drop the
     aspect ratio in favor of a viewport-aware clamp so tall screens
     still show a generous photo and shorter screens give the height
     back to the body. */
  @media (min-width: 768px) {
    .photo-wrap {
      aspect-ratio: auto;
      height: clamp(200px, 28vh, 280px);
    }
  }
  .photo-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .no-photo { width: 100%; height: 100%; }
  .rating-badge {
    position: absolute;
    /* The rating pill is ~22px tall; the ⋯ / × buttons on the right
       are 36px. Aligning tops puts them at the same Y but their
       centers diverge by ~7px and reads as misaligned. Bump the pill
       down by half the height difference so the visual horizon
       matches. */
    top: calc(0.75rem + 7px);
    left: 0.75rem;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  /* Bottom-right of the hero photo: toggle this POI as the trip's
     dashboard cover. Outlined star + "Set as cover" when inactive;
     filled star + "Cover" when this is the current cover. Visible
     only when editable and the POI actually has a photo (otherwise
     setting it as cover wouldn't render on the dashboard anyway). */
  .cover-badge {
    position: absolute;
    bottom: 0.75rem;
    right: 0.75rem;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 28px;
    padding: 0 0.625rem;
    border: 0;
    border-radius: 9999px;
    background: rgba(0, 0, 0, 0.6);
    color: #ffffff;
    font: inherit;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    backdrop-filter: blur(4px);
    transition: background 120ms ease;
  }
  .cover-badge:hover { background: rgba(0, 0, 0, 0.75); }
  .cover-badge--active {
    background: var(--color-primary);
    color: var(--color-on-primary);
  }
  .cover-badge--active:hover { background: var(--color-primary-active); }
  .cover-badge--active :global(svg) { fill: currentColor; }
  /* Floating overlay anchored to the top-right of the photo, holding
     the ⋯ trip-actions menu + the × close button. Both buttons share
     the same circular pill treatment so they read as siblings.
     Pushed below the 22px drag-handle on mobile so the buttons sit on
     the photo, not overlapping the grabber row. */
  .photo-overlay {
    position: absolute;
    top: calc(22px + 0.75rem);
    right: 0.75rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
  @media (min-width: 768px) {
    /* Drag handle is hidden on desktop — reclaim the offset so the
       buttons sit at the actual top of the photo. */
    .photo-overlay { top: 0.75rem; }
  }
  .close-btn {
    background: var(--color-canvas);
    color: var(--color-ink);
    border: 0;
    width: 36px;
    height: 36px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: rgba(0, 0, 0, 0.16) 0 4px 8px 0;
  }
  /* ActionsMenu trigger inherits its own styles; we just want it to
     visually sit on the photo with a comparable shadow. */
  .photo-overlay :global(.actions .trigger) {
    box-shadow: rgba(0, 0, 0, 0.16) 0 4px 8px 0;
  }

  .body {
    padding: 1.25rem 1.5rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    overflow-y: auto;
    flex: 1;
  }

  /* Title styling — applied via EditableTitle's `headingClass`.
     Use :global so the styles reach across component boundaries. */
  :global(.title) {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    color: var(--color-ink);
    line-height: 1.2;
    word-break: break-word;
  }
  .orig-name {
    margin: -0.25rem 0 0 0;
    font-size: 13px;
    color: var(--color-muted);
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  .reset-link {
    background: transparent;
    border: 0;
    color: var(--color-ink);
    font: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
    padding: 0;
  }
  .reset-link:hover { color: var(--color-primary-active); }

  .meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.375rem;
    font-size: 14px;
    color: var(--color-muted);
  }
  .address {
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
    color: var(--color-body);
  }

  /* Hours sits in the read-only metadata block, not the editable
     section. No top divider — that line now lives below as
     .section-divider, separating the read-only block from the
     user-editable controls (city, groups, notes). */
  .hours {
    margin-top: 0.125rem;
  }
  .section-divider {
    margin: 0.5rem 0 0;
    border: 0;
    border-top: 1px solid var(--color-hairline-soft);
  }
  .hours-title {
    margin: 0 0 0.375rem;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  /* Collapsed: today's line on the left, "See full schedule ▾" on
     the right. flex-wrap allows the toggle to drop below the today
     string on narrow widths where they don't fit side-by-side. */
  .hours-summary {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
    font-size: 13px;
    line-height: 1.4;
    color: var(--color-body);
  }
  .hours-today { font-weight: 500; }
  /* Same font-size as the today line so the two elements share the
     same optical center under align-items: baseline. Previously
     12px, which baseline-aligned but visually "dropped" against the
     13px today string. */
  .hours-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: transparent;
    border: 0;
    padding: 0;
    color: var(--color-muted);
    font: inherit;
    font-size: 13px;
    cursor: pointer;
    line-height: 1.4;
  }
  .hours-toggle:hover { color: var(--color-ink); }
  .hours-toggle--hide { margin-top: 0.375rem; }
  .hours-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.125rem;
    font-size: 13px;
    line-height: 1.4;
    color: var(--color-body);
  }
  .hours-row--today {
    color: var(--color-ink);
    font-weight: 600;
  }

  .groups { display: flex; flex-wrap: wrap; gap: 0.375rem; }
  .group-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: transparent;
    color: var(--color-muted);
    border: 1px solid var(--color-hairline);
    border-radius: 9999px;
    padding: 0.25rem 0.625rem;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    cursor: pointer;
    font-family: inherit;
    transition: background 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .group-chip:hover:not(:disabled):not(.active) {
    background: var(--color-surface-soft);
    color: var(--color-ink);
  }
  .group-chip.active {
    background: var(--color-ink);
    color: var(--color-on-dark);
    border-color: var(--color-ink);
  }
  .group-chip.active:hover:not(:disabled) {
    background: var(--color-body);
    border-color: var(--color-body);
  }
  .group-chip:disabled { cursor: wait; opacity: 0.6; }
  .group-chip .dot { width: 6px; height: 6px; flex: none; }

  .notes-section { display: flex; flex-direction: column; gap: 0.5rem; }
  .notes-head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
  .notes-head h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    color: var(--color-charcoal);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .notes {
    width: 100%;
    min-height: 96px;
    background: var(--color-surface-soft);
    color: var(--color-ink);
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    padding: 0.625rem 0.875rem;
    font: inherit;
    /* 16px to avoid iOS Safari auto-zoom-on-focus. */
    font-size: 16px;
    line-height: 1.5;
    resize: vertical;
    transition: background 100ms ease, border-color 100ms ease;
  }
  .notes::placeholder { color: var(--color-muted); }
  .notes:focus {
    outline: none;
    background: var(--color-canvas);
    border-color: var(--color-hairline);
  }
  .notes[readonly] { cursor: default; }

  /* Footer action bar: 3-4 icon-and-label tiles in an equal-width grid.
     Reads as a proper "more actions" tray instead of being buried at the
     bottom of the body under the notes textarea. */
  .quick-actions {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    gap: 0.375rem;
  }
  .quick-action {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-height: 52px;
    padding: 0.5rem 0.25rem;
    background: var(--color-surface-soft);
    border: 0;
    border-radius: var(--radius-sm);
    color: var(--color-ink);
    font: inherit;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.1;
    text-align: center;
    text-decoration: none;
    cursor: pointer;
    transition: background 100ms ease;
  }
  .quick-action:hover { background: var(--color-surface-strong); }
  .quick-action--destructive { color: var(--color-error); }
  .quick-action--destructive:hover { background: color-mix(in srgb, var(--color-error) 12%, transparent); }

  .detail-footer {
    flex: none;
    border-top: 1px solid var(--color-hairline-soft);
    background: var(--color-canvas);
    padding: 0.875rem 1.5rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  }
  .nav-btn { width: 100%; justify-content: center; min-height: 44px; height: 44px; padding: 0 1rem; font-size: 14px; }

</style>
